import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { CARD_BRANDS, CURRENCIES, cardLabel, cardExpiry } from './paymentConstants';
import { QuickCardForm } from './PaymentPickers';

// The company card list: what each card is called, when it expires and how many
// services still charge it. Removing a card with history only deactivates it —
// the server decides, and says which it did.

const CompanyCardManager = ({ canCreate, canEdit, canDelete, onClose, onChanged }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/subscriptions/cards?includeInactive=true');
            setCards(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load cards');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const changed = (message) => {
        setNotice(message || '');
        setError('');
        load();
        onChanged?.();
    };

    const startEdit = (card) => {
        setEditingId(card._id);
        setDraft({
            brand: card.brand,
            last4: card.last4,
            nickname: card.nickname || '',
            holderName: card.holderName || '',
            holderEmail: card.holderEmail || '',
            bankName: card.bankName || '',
            currency: card.currency || 'QAR',
            expiryMonth: card.expiryMonth || '',
            expiryYear: card.expiryYear || ''
        });
    };

    const saveEdit = async () => {
        try {
            await axiosInstance.put(`/api/subscriptions/cards/${editingId}`, {
                ...draft,
                expiryMonth: draft.expiryMonth || null,
                expiryYear: draft.expiryYear || null
            });
            setEditingId(null);
            changed('Card updated');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save the card');
        }
    };

    const setActive = async (card, isActive) => {
        try {
            await axiosInstance.put(`/api/subscriptions/cards/${card._id}`, { isActive });
            changed(isActive ? 'Card re-activated' : 'Card deactivated');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update the card');
        }
    };

    const remove = async (card) => {
        const warning = card.usageCount > 0
            ? `\n\n${card.usageCount} service(s) still charge this card — move them to the new card first.`
            : '';
        if (!window.confirm(`Remove ${cardLabel(card)}?${warning}\n\nA card with payment history is deactivated, not deleted.`)) return;
        try {
            const res = await axiosInstance.delete(`/api/subscriptions/cards/${card._id}`);
            changed(res.data.message);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove the card');
        }
    };

    const thisYear = new Date().getFullYear();
    const setD = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>💳 Company cards</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <p className="form-hint">
                        The cards your online services charge. Only the last 4 digits are kept. Add the expiry date and
                        you'll be warned two months before a card runs out under Meta, Google, Hostinger and the rest.
                    </p>
                    {error && <div className="alert alert-error">{error}</div>}
                    {notice && <div className="alert alert-success">{notice}</div>}

                    {loading ? (
                        <div className="loading-state">Loading cards...</div>
                    ) : (
                        <div className="card-list">
                            {cards.length === 0 && !adding && (
                                <div className="empty-state">No cards yet.</div>
                            )}
                            {cards.map(card => {
                                const exp = cardExpiry(card);
                                if (editingId === card._id) {
                                    return (
                                        <div key={card._id} className="card-row card-row-editing">
                                            <div className="quick-card-grid">
                                                <select className="form-select" value={draft.brand} onChange={(e) => setD('brand', e.target.value)}>
                                                    {CARD_BRANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                                                </select>
                                                <input className="form-input" value={draft.last4} inputMode="numeric"
                                                    onChange={(e) => setD('last4', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Last 4" />
                                                <input className="form-input" value={draft.nickname} onChange={(e) => setD('nickname', e.target.value)} placeholder="Nickname" />
                                                <div className="quick-card-expiry">
                                                    <select className="form-select" value={draft.expiryMonth} onChange={(e) => setD('expiryMonth', e.target.value)}>
                                                        <option value="">MM</option>
                                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                                            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                                        ))}
                                                    </select>
                                                    <select className="form-select" value={draft.expiryYear} onChange={(e) => setD('expiryYear', e.target.value)}>
                                                        <option value="">YYYY</option>
                                                        {Array.from({ length: 14 }, (_, i) => thisYear - 2 + i).map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <input className="form-input" value={draft.holderName} onChange={(e) => setD('holderName', e.target.value)} placeholder="Card holder" />
                                                <input className="form-input" type="email" value={draft.holderEmail} onChange={(e) => setD('holderEmail', e.target.value)} placeholder="Holder email" />
                                                <input className="form-input" value={draft.bankName} onChange={(e) => setD('bankName', e.target.value)} placeholder="Bank" />
                                                <select className="form-select" value={draft.currency} onChange={(e) => setD('currency', e.target.value)}>
                                                    {CURRENCIES.map(c => <option key={c} value={c}>{c} statement</option>)}
                                                </select>
                                            </div>
                                            <div className="quick-card-actions">
                                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                                                <button className="btn btn-sm btn-primary" onClick={saveEdit}>Save</button>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={card._id} className={`card-row ${card.isActive ? '' : 'card-row-inactive'}`}>
                                        <div className={`card-face card-face-${card.brand}`}>
                                            <span className="card-face-brand">{CARD_BRANDS.find(b => b.value === card.brand)?.label || 'Card'}</span>
                                            <span className="card-face-number">•••• {card.last4}</span>
                                        </div>
                                        <div className="card-row-main">
                                            <div className="card-row-title">
                                                {card.nickname || cardLabel(card)}
                                                {!card.isActive && <span className="status-badge status-muted">Inactive</span>}
                                                {exp?.expired && <span className="status-badge status-overdue">Expired {exp.text}</span>}
                                                {exp?.soon && <span className="status-badge status-due-soon">Expires {exp.text}</span>}
                                            </div>
                                            <div className="pay-card-sub">
                                                {[card.holderName, card.holderEmail, card.bankName, exp && !exp.expired && !exp.soon ? `Expires ${exp.text}` : '', `${card.currency || 'QAR'} statement`]
                                                    .filter(Boolean).join(' · ')}
                                            </div>
                                            <div className="pay-card-sub">
                                                {[
                                                    card.usageCount > 0
                                                        ? `Default card for ${card.usageCount} live service${card.usageCount === 1 ? '' : 's'}`
                                                        : 'No live service defaults to it',
                                                    card.paymentCount > 0
                                                        ? `${card.paymentCount} payment${card.paymentCount === 1 ? '' : 's'} recorded`
                                                        : 'no payments recorded yet'
                                                ].join(' · ')}
                                            </div>
                                        </div>
                                        <div className="card-row-actions">
                                            {canEdit && <button className="btn btn-sm btn-secondary" onClick={() => startEdit(card)}>Edit</button>}
                                            {canEdit && (card.isActive
                                                ? <button className="btn btn-sm btn-ghost" onClick={() => setActive(card, false)}>Deactivate</button>
                                                : <button className="btn btn-sm btn-ghost" onClick={() => setActive(card, true)}>Re-activate</button>)}
                                            {canDelete && <button className="btn btn-sm btn-danger" onClick={() => remove(card)}>Remove</button>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {canCreate && (adding ? (
                        <QuickCardForm onCancel={() => setAdding(false)} onCreated={() => { setAdding(false); changed('Card added'); }} />
                    ) : (
                        <button className="btn btn-primary" onClick={() => setAdding(true)}>+ Add card</button>
                    ))}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default CompanyCardManager;
